import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  InputBase,
  Badge,
  Tooltip,
  useMediaQuery,
} from "@material-ui/core";
import HomeOutlinedIcon from "@material-ui/icons/HomeOutlined";
import SearchOutlinedIcon from "@material-ui/icons/SearchOutlined";
import { useCart } from "../Context/CartContext";
import { useItemSearchInput } from "../Context/ItemSearchContext";
import { useOrderSession } from "../Context/OrderSessionContext";
import AddShoppingCartOutlinedIcon from "@material-ui/icons/AddShoppingCartOutlined";
import ArrowBackOutlinedIcon from "@material-ui/icons/ArrowBackOutlined";
import MenuIcon from "@material-ui/icons/Menu";
import ExitToAppOutlinedIcon from "@material-ui/icons/ExitToAppOutlined";
import ClearIcon from "@material-ui/icons/Clear";
import styles from "./AppBarComp.module.css";
import { CART_DRAWER_WIDTH } from "./cartLayout";
import { useNavDrawer } from "../Context/NavDrawerContext";
import { useEffectiveProfile } from "../utils/useEffectiveProfile";
import { useSnackbar } from "../utils/useSnackbar";
import ConfirmationDialog from "../components/ConfirmationDialog/ConfirmationDialog";
import { beginLogout, hasUnsavedChanges, LOGOUT_UNSAVED_MESSAGE } from "../utils/logoutGuard";
import GridLayoutSelector from "./GridLayoutSelector";
import ExpiryBell from "../components/ExpiryBell/ExpiryBell";
import PosUserMenu from "./PosUserMenu";
import { formatInvoiceNumber } from "../utils/docNumber";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: "#ef7f1a",
    backgroundImage: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
    boxShadow: "0 2px 12px rgba(234, 88, 12, 0.28)",
    zIndex: 1100,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${CART_DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: CART_DRAWER_WIDTH,
  },
  toolbar: {
    minHeight: 54,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      minHeight: 48,
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
      gap: theme.spacing(0.5),
    },
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    padding: 6,
    color: "#ffffff",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      transform: "translateY(-1px)",
    },
    [theme.breakpoints.down("xs")]: {
      width: 32,
      height: 32,
      padding: 4,
    },
  },
  title: {
    flexGrow: 1,
    minWidth: 0,
    fontSize: "clamp(1rem, 1.6vw, 1.25rem)",
    fontWeight: 800,
    letterSpacing: "-0.01em",
    color: "#ffffff",
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.15)",
  },
  editBreadcrumbTrail: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    flexGrow: 1,
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  breadcrumbLink: {
    color: "#fed7aa",
    fontSize: "clamp(0.75rem, 1.2vw, 0.875rem)",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    transition: "color 0.15s",
    "&:hover": {
      color: "#ffffff",
      textDecoration: "underline",
    },
  },
  breadcrumbSep: {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.85rem",
    userSelect: "none",
    flexShrink: 0,
  },
  breadcrumbCurrent: {
    color: "#ffffff",
    fontSize: "clamp(0.75rem, 1.2vw, 0.875rem)",
    fontWeight: 800,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    padding: "2px 8px",
    borderRadius: 6,
    border: "1px solid rgba(255, 255, 255, 0.25)",
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  hide: {
    display: "none !important",
  },
  search: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#ffffff",
    width: "100%",
    maxWidth: "380px",
    height: 38,
    flexShrink: 1,
    minWidth: 0,
    border: "1.5px solid rgba(0, 0, 0, 0.08)",
    borderRadius: 10,
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#ffffff",
      borderColor: "rgba(0, 0, 0, 0.18)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    },
    "&:focus-within": {
      borderColor: "#ffffff",
      boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.7), 0 3px 10px rgba(0, 0, 0, 0.12)",
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: 130,
      height: 34,
      borderRadius: 8,
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    cursor: "pointer",
  },
  inputRoot: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    fontSize: "0.875rem",
  },
  inputInput: {
    width: "100%",
    padding: "6px 8px 6px 0",
    color: "#1e293b",
    fontSize: "0.875rem",
    "&::placeholder": {
      color: "#94a3b8",
      opacity: 1,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8125rem",
      padding: "4px 4px 4px 0",
    },
  },
  clearBtn: {
    padding: 3,
    marginRight: 4,
    color: "#94a3b8",
    "&:hover": {
      color: "#475569",
    },
  },
  cartBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ffffff !important",
      color: "#c2410c !important",
      fontWeight: 900,
      fontSize: "0.78rem",
      height: 20,
      minWidth: 20,
      padding: "0 5px",
      borderRadius: "10px",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.35)",
      border: "1.5px solid #ea580c",
    },
  },
}));

const AppBarComp = ({
  open,
  setOpen,
  invoice,
  isCartPersistent,
  gridLayout,
  onGridLayoutChange,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isNavigationDrawer = useMediaQuery(theme.breakpoints.down("sm"));
  const { cartItems, handleAddToCart, setCartItems } = useCart();
  const { searchInput, setSearchInput, clearSearch } = useItemSearchInput();
  const {
    setMobileNavOpen,
    desktopNavVisible,
    setDesktopNavVisible,
  } = useNavDrawer() || {};
  const { clearActiveSession } = useOrderSession();
  const history = useHistory();
  const location = useLocation();
  const isPaymentView = location.pathname.endsWith("/payment");
  const goBackFromPayment = () => history.push(location.pathname.replace(/\/payment$/, ""));
  const isEditInvoice = location.pathname.startsWith("/edit/invoice");
  const invoiceDocNumber = formatInvoiceNumber(invoice?.invoiceNumber);
  const originPath = location?.state?.from || (invoice?._id ? `/invoice/${invoice._id}` : "/invoices");
  const originLabel = location?.state?.fromLabel || "Invoices";
  const dispatch = useDispatch();

  // Leaving an in-progress/edited invoice behind should prompt if dirty,
  // and clean up cart/invoice state upon navigation to PosMainScreen.
  const goToPosMainScreen = () => {
    history.push("/PosMainScreen");
  };
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  // POS is the only screen that swaps the app-wide Header out for this bar, so
  // without this the till had no way to sign out without navigating away first.
  // Behaviour is deliberately identical to Header's own logout (dispatch
  // LOGOUT, toast, push to "/") behind the same ConfirmationDialog the
  // SuperAdmin navbar uses - a mis-tap on a touchscreen shouldn't end a shift.
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [openSnackbar] = useSnackbar();
  const handleLogout = () => {
    beginLogout();
    // Close first: the dialog outlives the dispatch (no page reload here), and
    // a stranded backdrop swallows every click - see SuperAdminNavbar.
    setLogoutConfirmOpen(false);
    dispatch({ type: "LOGOUT" });
    openSnackbar("Logged out successfully");
    history.push("/");
  };
  const cartItemCount = () => cartItems.length;
  const handleNavigationToggle = () => {
    if (isNavigationDrawer) {
      setMobileNavOpen?.((isOpen) => !isOpen);
      return;
    }

    setDesktopNavVisible?.((isVisible) => !isVisible);
  };

  // Mirrors the main Header so the same control is described the same way on
  // both screens, and the label follows what the click will actually do.
  const navToggleLabel = isNavigationDrawer
    ? "Show navigation menu"
    : desktopNavVisible
    ? "Hide navigation bar"
    : "Show navigation bar";

  // A till is a touchscreen: MUI's default 700ms long-press means a tap fires
  // the click before a tooltip ever opens, so show it immediately on touch.
  const tooltipProps = {
    arrow: true,
    enterDelay: 150,
    enterTouchDelay: 0,
    leaveTouchDelay: 3000,
  };
  const effectiveProfile = useEffectiveProfile();
  return (
    <AppBar
      position="fixed"
      className={`${classes.appBar} ${open && isCartPersistent ? classes.appBarShift : ""}`}
    >
      <Toolbar className={classes.toolbar}>
        <Tooltip title={navToggleLabel} {...tooltipProps}>
          <IconButton
            color="inherit"
            className={classes.iconButton}
            aria-label={navToggleLabel}
            onClick={handleNavigationToggle}
          >
            <MenuIcon style={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
        {isPaymentView ? (
          <Tooltip title="Back to items" {...tooltipProps}>
            <IconButton
              className={classes.iconButton}
              aria-label="Back to items"
              onClick={goBackFromPayment}
            >
              <ArrowBackOutlinedIcon style={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        ) : isEditInvoice ? (
          <Tooltip title={`Back to ${originLabel}`} {...tooltipProps}>
            <IconButton
              className={classes.iconButton}
              aria-label={`Back to ${originLabel}`}
              onClick={() => history.push(originPath)}
            >
              <ArrowBackOutlinedIcon style={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Go to Point of Sale" {...tooltipProps}>
            <IconButton
              className={classes.iconButton}
              aria-label="Go to Point of Sale"
              onClick={goToPosMainScreen}
            >
              <HomeOutlinedIcon style={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        )}
        {isEditInvoice ? (
          <div className={classes.editBreadcrumbTrail}>
            <span
              className={classes.breadcrumbLink}
              onClick={goToPosMainScreen}
              title="Point of Sale (New Sale)"
            >
              Home
            </span>
            <span className={classes.breadcrumbSep}>›</span>
            {location?.state?.rootFrom && (
              <>
                <span
                  className={classes.breadcrumbLink}
                  onClick={() => history.push(location.state.rootFrom)}
                  title={`Go to ${location.state.rootFromLabel || "Back"}`}
                >
                  {location.state.rootFromLabel || "Invoices"}
                </span>
                <span className={classes.breadcrumbSep}>›</span>
              </>
            )}
            <span
              className={classes.breadcrumbLink}
              onClick={() => history.push(originPath)}
              title={`Go to ${originLabel}`}
            >
              {originLabel}
            </span>
            <span className={classes.breadcrumbSep}>›</span>
            <span className={classes.breadcrumbCurrent}>
              {invoiceDocNumber ? `Edit ${invoiceDocNumber}` : "Edit Invoice"}
            </span>
            {isPaymentView && (
              <>
                <span className={classes.breadcrumbSep}>›</span>
                <span className={classes.breadcrumbCurrent}>Payment</span>
              </>
            )}
          </div>
        ) : (
          <Typography variant="h6" noWrap className={classes.title}>
            {isPaymentView ? "Payment" : effectiveProfile?.businessName}
          </Typography>
        )}
        <div className={classes.search}>
          <div className={classes.searchIcon}>
            <SearchOutlinedIcon style={{ fontSize: 20 }} />
          </div>
          <InputBase
            placeholder="Search item or barcode..."
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
            }}
            // data-pos-search lets ItemCards' barcode listener tell when a
            // scan was typed into this box (and so should be cleared).
            inputProps={{ "aria-label": "Search item or barcode", "data-pos-search": "true" }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput ? (
            <IconButton
              size="small"
              className={classes.clearBtn}
              onClick={clearSearch}
              aria-label="Clear search"
            >
              <ClearIcon style={{ fontSize: 16 }} />
            </IconButton>
          ) : null}
        </div>
        {!isPaymentView && (
          <GridLayoutSelector
            gridLayout={gridLayout}
            onGridLayoutChange={onGridLayoutChange}
          />
        )}
        <ExpiryBell onDark />
        <Tooltip title="Open cart" {...tooltipProps}>
          <IconButton
            color="inherit"
            aria-label="Open cart"
            onClick={handleDrawerOpen}
            className={`${classes.iconButton} ${open ? classes.hide : ""}`}
          >
            <Badge
              badgeContent={cartItemCount()}
              color="secondary"
              overlap="rectangular"
              className={classes.cartBadge}
            >
              <AddShoppingCartOutlinedIcon style={{ fontSize: 22 }} />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title="Log out" {...tooltipProps}>
          <IconButton
            color="inherit"
            aria-label="Log out"
            onClick={() => setLogoutConfirmOpen(true)}
            className={`${classes.iconButton} ${open ? classes.hide : ""}`}
          >
            <ExitToAppOutlinedIcon style={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
        <span className={open ? classes.hide : undefined} style={{ display: "inline-flex" }}>
          <PosUserMenu onLogout={() => setLogoutConfirmOpen(true)} />
        </span>
      </Toolbar>

      <ConfirmationDialog
        open={logoutConfirmOpen}
        title="Log out?"
        message={
          hasUnsavedChanges()
            ? LOGOUT_UNSAVED_MESSAGE
            : "You'll need to sign in again to get back to the till."
        }
        confirmLabel="Log out"
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
      />
    </AppBar>
  );
};

export default AppBarComp;
